import * as amplitude from '@amplitude/analytics-browser';

import { getTSPByKey } from '../tsps';
import { OAuthConfig } from '../tsps/types';

/**
 * OAuth Service - Generic OAuth management
 *
 * This service provides a generic OAuth flow that adapts to each provider's
 * specific requirements based on their TSP configuration. All provider-specific
 * logic is contained within the individual TSP config files.
 */
export class OAuthService {
  private static instance: OAuthService;

  private constructor() {}

  static getInstance(): OAuthService {
    if (!OAuthService.instance) {
      OAuthService.instance = new OAuthService();
    }
    return OAuthService.instance;
  }

  /**
   * Get OAuth config for a provider from TSP config
   */
  private getProviderConfig(providerKey: string): OAuthConfig | null {
    const tspConfig = getTSPByKey(providerKey);
    return tspConfig?.oauth || null;
  }

  /**
   * Initiate OAuth flow for a provider
   */
  async initiateOAuth(params: {
    providerKey: string;
    clientID: string;
    serviceName: string;
    linkState: string;
    onError?: (error: string) => void;
  }): Promise<void> {
    const { providerKey, clientID, serviceName, linkState, onError } = params;

    try {
      // Get OAuth config from TSP config
      const oauthConfig = this.getProviderConfig(providerKey);
      if (!oauthConfig) {
        const error = `OAuth configuration not found for ${providerKey}`;
        console.error(`[OAuthService] ${error}`);
        onError?.(error);
        return;
      }

      // Validate client ID
      if (!clientID) {
        const tspConfig = getTSPByKey(providerKey);
        const error = `No ${
          tspConfig?.name || providerKey
        } app is associated with this account - please contact Axle support`;
        console.error(`[OAuthService] ${error}`);
        onError?.(error);
        return;
      }

      // Track OAuth initiation
      amplitude.track('OAuth Redirect Initiated', {
        tsp: providerKey,
        serviceName,
      });

      // Build OAuth URL using TSP configuration
      const oauthUrl = this.buildOAuthUrl({
        oauthConfig,
        clientID,
        serviceName,
        linkState,
        providerKey,
      });

      // Redirect to OAuth provider
      window.location.replace(oauthUrl);
    } catch (error) {
      const errorMessage = `Failed to initiate OAuth for ${providerKey}: ${error}`;
      console.error(`[OAuthService] ${errorMessage}`, error);
      onError?.(errorMessage);
    }
  }

  /**
   * Parse OAuth callback parameters
   */
  parseOAuthCallback(searchParams: URLSearchParams): {
    success: boolean;
    code?: string;
    state?: string;
    error?: {
      code: string;
      description?: string;
      uri?: string;
    };
  } {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorCode = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const errorUri = searchParams.get('error_uri');

    if (errorCode) {
      return {
        success: false,
        state: state || undefined,
        error: {
          code: errorCode,
          description: errorDescription || undefined,
          uri: errorUri || undefined,
        },
      };
    }

    if (code && state) {
      return {
        success: true,
        code,
        state,
      };
    }

    return {
      success: false,
      error: {
        code: 'invalid_callback',
        description: 'Missing required parameters in OAuth callback',
      },
    };
  }

  /**
   * Get user-friendly error message from OAuth error
   */
  getErrorMessage(
    error: { code: string; description?: string },
    providerName: string
  ): {
    title: string;
    message: string;
  } {
    switch (error.code) {
      case 'access_denied':
        return {
          title: `${providerName} access denied`,
          message:
            'You denied access to your account. Please try again and grant the necessary permissions.',
        };
      case 'invalid_request':
        return {
          title: `${providerName} authorization error`,
          message:
            'There was a problem with the authorization request. Please try again.',
        };
      case 'unauthorized_client':
        return {
          title: `${providerName} client not authorized`,
          message:
            'The application is not authorized to access your account. Please contact support.',
        };
      case 'unsupported_response_type':
      case 'invalid_scope':
        return {
          title: `${providerName} configuration error`,
          message:
            'There was a configuration problem. Please contact support for assistance.',
        };
      case 'server_error':
      case 'temporarily_unavailable':
        return {
          title: `${providerName} temporarily unavailable`,
          message:
            'The service is temporarily unavailable. Please try again in a few minutes.',
        };
      case 'invalid_callback':
        return {
          title: `${providerName} callback error`,
          message:
            'There was a problem processing the authorization response. Please try again.',
        };
      default:
        return {
          title: `${providerName} authorization failed`,
          message: error.description
            ? decodeURIComponent(error.description)
            : 'There was a problem authorizing access to your account.',
        };
    }
  }

  /**
   * Get all available OAuth providers
   */
  getProviders(): Record<string, OAuthConfig> {
    // This method is probably not needed anymore since configs are in TSP files
    // But keeping for backward compatibility
    const providers: Record<string, OAuthConfig> = {};
    // Could implement by iterating through all TSP configs if needed
    return providers;
  }

  /**
   * Get specific provider configuration
   */
  getProvider(providerKey: string): OAuthConfig | null {
    return this.getProviderConfig(providerKey);
  }

  /**
   * Build OAuth authorization URL using TSP configuration
   */
  private buildOAuthUrl(params: {
    oauthConfig: OAuthConfig;
    clientID: string;
    serviceName: string;
    linkState: string;
    providerKey: string;
  }): string {
    const { oauthConfig, clientID, serviceName, linkState, providerKey } =
      params;

    // Get redirect URI
    const redirectUri = oauthConfig.getRedirectUri(linkState);

    // Get scope (only if provider requires it and scopes are configured)
    const scope = oauthConfig.getScope
      ? oauthConfig.getScope({ serviceName })
      : oauthConfig.defaultScopes?.[serviceName] ||
        oauthConfig.defaultScopes?.default ||
        '';

    // Use custom URL builder if provided
    if (oauthConfig.urlBuildingOptions?.customUrlBuilder) {
      return oauthConfig.urlBuildingOptions.customUrlBuilder({
        authUrl: oauthConfig.authUrl,
        clientID,
        scope,
        redirectUri,
        linkState,
        requiredParams: oauthConfig.requiredParams,
      });
    }

    // Use configuration-driven URL building
    return this.buildConfigurableOAuthUrl({
      oauthConfig,
      clientID,
      scope,
      redirectUri,
      linkState,
      providerKey,
    });
  }

  /**
   * Build OAuth URL using configuration options
   */
  private buildConfigurableOAuthUrl(params: {
    oauthConfig: OAuthConfig;
    clientID: string;
    scope: string;
    redirectUri: string;
    linkState: string;
    providerKey: string;
  }): string {
    const { oauthConfig, clientID, scope, redirectUri, linkState } = params;
    const options = oauthConfig.urlBuildingOptions;

    // If scope encoding should be disabled, build URL manually
    if (options?.disableScopeEncoding && scope) {
      const urlParams: string[] = [];
      urlParams.push(`client_id=${encodeURIComponent(clientID)}`);
      urlParams.push(`response_type=code`);

      if (scope && oauthConfig.requiredParams.includes('scope')) {
        urlParams.push(`scope=${scope}`); // No encoding for scopes
      }
      if (
        !options.usePreConfiguredRedirectUri &&
        oauthConfig.requiredParams.includes('redirect_uri')
      ) {
        urlParams.push(`redirect_uri=${encodeURIComponent(redirectUri)}`);
      }
      if (oauthConfig.requiredParams.includes('state')) {
        urlParams.push(`state=${encodeURIComponent(linkState)}`);
      }

      return `${oauthConfig.authUrl}?${urlParams.join('&')}`;
    }

    // Standard URL building with URLSearchParams
    const urlParams = new URLSearchParams();
    urlParams.append('client_id', clientID);
    urlParams.append('response_type', 'code');

    // Add optional parameters based on provider requirements
    if (scope && oauthConfig.requiredParams.includes('scope')) {
      urlParams.append('scope', scope);
    }
    if (
      !options?.usePreConfiguredRedirectUri &&
      oauthConfig.requiredParams.includes('redirect_uri')
    ) {
      urlParams.append('redirect_uri', redirectUri);
    }
    if (oauthConfig.requiredParams.includes('state')) {
      urlParams.append('state', linkState);
    }

    return `${oauthConfig.authUrl}?${urlParams.toString()}`;
  }
}

// Export singleton instance
export const oauthService = OAuthService.getInstance();
